// client/src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getNFTs, getCurrentUser, logout } from './services/api';
import Navbar from './Components/Navbar';
import Sidebar from './Components/Sidebar';
import SearchBar from './Components/SearchBar';
import ProductGrid from './Components/ProductGrid';
import ProductDetail from './Components/ProductDetail';
import UserCollection from './Components/UserCollection';
import Login from './Components/Login';
import Register from './Components/Register';
import './index.css';
import CreateNFT from './Components/CreateNFT';

// Sample data for testing
const sampleNFTs = [

{
  id: 7,
  title: "Galactic Voyager X-7",
  image: "/images/nfts/spaceship.jpg",
  creator: {
    name: "SpaceCollector",
    avatar: "/api/placeholder/40/40"
  },
  price: 2.8,
  rarity: "legendary",
  description: "A collector's edition model of the famous Galactic Voyager X-7 spaceship from the cosmic wars era. Features detailed thrusters and authentic battle damage.",
  category: "Collectibles",
  likes: 312,
  views: 4560,
  created: "2025-02-10",
  model: {
    type: "obj",
    path: "/build/assets/Resources/Models/voyager_spaceship.prefab",
    scale: 0.015,
    position: [0, 0, 0],
    rotation: [0, 45, 0]
  }
},
{
  id: 8,
  title: "1957 Quantum Roadster",
  image: "/images/nfts/roadster.jpg",
  creator: {
    name: "ClassicAutoVault",
    avatar: "/api/placeholder/40/40"
  },
  price: 3.5,
  rarity: "epic",
  description: "A meticulously detailed digital twin of the iconic 1957 Quantum Roadster. Complete with functioning doors, hood, and trunk. The pinnacle of vintage automotive design.",
  category: "Collectibles",
  likes: 287,
  views: 3950,
  created: "2025-03-15",
  model: {
    type: "fbx",
    path: "/build/assets/Resources/Models/vintage_car.prefab",
    scale: 0.01,
    position: [0, -0.5, 0],
    rotation: [0, 180, 0]
  }
},
{
  id: 9,
  title: "Ultra Guardian Action Figure",
  image: "/images/nfts/ironman.jpg",
  creator: {
    name: "RetroToyCo",
    avatar: "/api/placeholder/40/40"
  },
  price: 0.95,
  rarity: "rare",
  description: "A digital recreation of the classic Ultra Guardian action figure from the 2010s. Features 16 points of articulation and comes with miniature weapons and accessories.",
  category: "Collectibles",
  likes: 176,
  views: 2340,
  created: "2025-03-22",
  model: {
    type: "glb",
    path: "/build/assets/Resources/Models/action_figure.prefab",
    scale: 0.2,
    position: [0, 0, 0],
    rotation: [0, 0, 0]
  }
},
{
  id: 10,
  title: "Building",
  image: "/images/nfts/building.jpg",
  creator: {
    name: "VintageToyVault",
    avatar: "/api/placeholder/40/40"
  },
  price: 1.3,
  rarity: "uncommon",
  description: "A digital rendition of the building from the 1980s. The 3D model perfectly captures the tin craftsmanship.",
  category: "Collectibles",
  likes: 145,
  views: 1870,
  created: "2025-04-01",
  model: {
    type: "obj",
    path: "/build/assets/Resources/Models/wind_up_toy.prefab",
    scale: 0.025,
    position: [0, -0.2, 0],
    rotation: [0, 45, 0]
  }
},
{
  id: 11,
  title: "Nebula Cruiser Deluxe",
  image: "/images/nfts/nebulacruise.jpg",
  creator: {
    name: "CosmicToyDesigns",
    avatar: "/api/placeholder/40/40"
  },
  price: 2.2,
  rarity: "epic",
  description: "The flagship of the Galactic Fleet, this collector's Nebula Cruiser features illuminated engine effects and detailed interior command bridge. Limited edition digital asset.",
  category: "Artwork",
  likes: 231,
  views: 3175,
  created: "2025-03-05",
  model: {
    type: "glb",
    path: "/build/assets/Resources/Models/nebula_cruiser.prefab",
    scale: 0.012,
    position: [0, 0, 0],
    rotation: [0, 30, 0]
  }
}
];

const categories = [
  "All",
  "Artwork",
  "Abstract",
  "Landscape",
  "Avatar",
  "Character",
  "Animation",
  "Music",
  "Collectibles"
];

// Protected route component
const ProtectedRoute = ({ children, isAuthenticated }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  // Load NFTs from localStorage on initial render
  const loadInitialNFTs = () => {
    const storedNFTs = JSON.parse(localStorage.getItem('mockNFTs') || '[]');
    return [...sampleNFTs, ...storedNFTs];
  };

  const [products, setProducts] = useState(loadInitialNFTs());
  const [filteredProducts, setFilteredProducts] = useState(loadInitialNFTs());
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [user, setUser] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const currentUser = getCurrentUser(); // Use function from api.js
    if (currentUser) {
      setUser(currentUser);
    }
    setIsInitialized(true);
  }, []);

  // Comment out the original API-based fetch
  // useEffect(() => {
  //   async function fetchProducts() {
  //     try {
  //       const nfts = await getNFTs(); // Use function from api.js
  //       setProducts(nfts);
  //       setFilteredProducts(nfts); // Ensure we set the filtered products too
  //     } catch (error) {
  //       console.error("Error fetching NFTs:", error);
  //     }
  //   }
  //   
  //   fetchProducts();
  // }, []);

  useEffect(() => {
    let result = products;
    
    // Apply category filter
    if (selectedCategory !== "All") {
      result = result.filter(product => product.category === selectedCategory);
    }
    
    // Apply search term
    if (searchTerm) {
      result = result.filter(product => 
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.creator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredProducts(result);
  }, [products, selectedCategory, searchTerm]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleWalletConnect = (address) => {
    setIsWalletConnected(true);
    setWalletAddress(address);
  };

  const handleWalletDisconnect = () => {
    setIsWalletConnected(false);
    setWalletAddress("");
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    logout(); // Using the imported function from api.js
    setUser(null);
  };

  // Simplified NFT creation handler using localStorage
  const handleCreateNFT = (newNFT) => {
    // Add the new NFT to the products array
    const updatedProducts = [newNFT, ...products];
    setProducts(updatedProducts);
    
    // Also store in localStorage for persistence
    const storedNFTs = JSON.parse(localStorage.getItem('mockNFTs') || '[]');
    storedNFTs.push(newNFT);
    localStorage.setItem('mockNFTs', JSON.stringify(storedNFTs));
  };

  const isLoggedIn = !!user;

  return (
    <Router>
      <div className="app">
        <Navbar 
          isWalletConnected={isWalletConnected}
          walletAddress={walletAddress}
          onConnect={handleWalletConnect}
          onDisconnect={handleWalletDisconnect}
          user={user}
          onLogout={handleLogout}
        />
        
        <Routes>
          <Route path="/" element={
            <>
              <SearchBar onSearch={handleSearch} />
              <div className="main-layout">
                <Sidebar 
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                />
                <div className="product-content">
                  <ProductGrid products={filteredProducts} />
                </div>
              </div>
            </>
          } />
          
          <Route path="/nft/:id" element={
            <ProductDetail 
              products={products} 
              isWalletConnected={isWalletConnected}
              onConnect={handleWalletConnect}
              isLoggedIn={isLoggedIn}
              userId={user?.id}
            />
          } />

          <Route path="/collection" element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              <UserCollection userId={user?.id} />
            </ProtectedRoute>
          } />

          <Route path="/login" element={
            isLoggedIn ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
          } />

          <Route path="/register" element={
            isLoggedIn ? <Navigate to="/" replace /> : <Register onRegister={handleLogin} />
          } />

          {/* Fixed duplicate route and passed the handler function */}
          <Route path="/create-nft" element={
            <ProtectedRoute isAuthenticated={isLoggedIn}>
              <CreateNFT onCreateNFT={handleCreateNFT} />
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;